import React from 'react';
import { compose, withHandlers, withProps } from 'recompose';
import { connect } from '../../redux/store';
import {
  ActivityFeed,
  EmptyMessage,
  ErrorMessage,
  LoadingMessage,
  PaginationBar,
  C,
} from '@kineticdata/bundle-common';
import { searchSubmissions, defineKqlQuery, I18n } from '@kineticdata/react';
import { getSubmissionPath } from '../../utils';
import { SurveyCard } from '../shared/SurveyCard';
import { Map } from 'immutable';

const buildSearch = profile =>
  defineKqlQuery()
    .equals('type', 'type')
    .equals('coreState', 'coreState')
    .in(`values[${C.FIELD_ASSIGNED_INDIVIDUAL}]`, 'user')
    .end()({
    type: C.SURVEY_FORM_TYPE,
    coreState: C.CORE_STATE_DRAFT,
    user: [profile.email, profile.username],
  });

const ActivityFeedContent = props => (
  <>
    {!props.loading &&
      props.errors && (
        <div className="alert alert-danger alert-bar">
          {Map(props.errors)
            .map((error, key) => (
              <p key={key}>
                <div>
                  <I18n>{`Failed to retrieve surveys`}</I18n>
                </div>
                <small className="text-muted">{error.message}</small>
              </p>
            ))
            .toList()}
        </div>
      )}
    {props.data ? (
      <>
        {!props.loading &&
          !props.errors &&
          props.data.length === 0 && (
            <EmptyMessage
              title="No surveys found"
              message="Your assigned surveys will appear here"
            />
          )}
        {props.dataElements}
        {!props.hidePaging && (
          <PaginationBar
            className="mb-3"
            paging={props.loading}
            previousPage={
              props.hasPreviousPage ? props.getPreviousPage : undefined
            }
            nextPage={props.hasNextPage ? props.getNextPage : undefined}
            startIndex={props.startIndex}
            endIndex={props.endIndex}
            count={props.count}
            countDone={true}
            pageSize={props.options.pageSize}
            changePageSize={props.options.changePageSize}
          />
        )}
      </>
    ) : props.loading ? (
      <LoadingMessage />
    ) : (
      <ErrorMessage message="An unexpected error ocurred." />
    )}
  </>
);

const SurveyActivityComponent = props => (
  <ActivityFeed
    feedKey={props.feedKey}
    uncontrolled
    pageSize={props.pageSize}
    joinByDirection="DESC"
    joinBy="createdAt"
    dataSources={props.dataSources}
    showCount={true}
    options={{ pageSize: props.pageSize, changePageSize: props.changePageSize }}
  >
    {ActivityFeedContent}
  </ActivityFeed>
);

export const SurveyActivity = compose(
  connect((state, props) => ({
    setHomeSurveysCount: state.app.actions.setHomeSurveysCount,
    kappSlug: state.app.kapp.slug,
    profile: state.app.profile,
    appLocation: state.app.location,
    coreState: C.CORE_STATE_DRAFT,
  })),
  withHandlers({
    buildRequestCard: props => record => (
      <SurveyCard
        key={record.id}
        submission={record}
        username={props.profile.username}
        appLocation={props.appLocation}
        navigate={props.navigate}
        path={getSubmissionPath(props.appLocation, record, null)}
      />
    ),
    changePageSize: props =>
      !!props.setPageSize
        ? value => () => {
            props.setPageSize(value);
          }
        : undefined,
  }),
  withProps(props => ({
    // The sources for the data shown in the activity feed
    dataSources: {
      surveys: {
        fn: searchSubmissions,
        params: (prevParams, prevResult) =>
          prevParams && prevResult
            ? prevResult.nextPageToken
              ? {
                  ...prevParams,
                  pageToken: prevResult.nextPageToken,
                  count: false,
                }
              : null
            : {
                kapp: props.kappSlug,
                limit: Math.max(props.chunkSize || props.pageSize || 25, 25),
                count: true,
                search: {
                  q: buildSearch(props.profile),
                  include: [
                    'details',
                    'values',
                    'form',
                    'form.attributes',
                    'form.attributesMap',
                    'form.kapp',
                    'form.kapp.attributes',
                  ],
                },
              },
        transform: result => {
          // Update the count if it was fetched
          if (
            typeof result.count === 'number' &&
            typeof props.setHomeSurveysCount === 'function'
          ) {
            props.setHomeSurveysCount(result.count);
          }
          return {
            data: result.submissions,
            nextPageToken: result.nextPageToken,
          };
        },
        component: props.buildRequestCard,
      },
    },
  })),
)(SurveyActivityComponent);
